import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
  CardAction,
} from '@/components/ui/card';
import { useEffect } from 'react';
import MarkdownDisplay from '@/components/MarkdownDisplay';
import { Separator } from '@/components/ui/separator';
import StatusBadge from '@/components/StatusBadge';
import { getTeamStatusDisplay, optimisticTeamStatus } from '@/utils/status';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Timer, Proportions, Hd, Play, Video, Layers } from 'lucide-react';
import UploadVersion from './UploadVersion';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogClose,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useFetcher } from 'react-router';
import type { Database } from '@/types/database.types';
import type { ProjectRole } from '@/utils/status';
import type { ActionResult, DeliverableWithTeam } from '..';

import { toast } from 'sonner';
type Project = Database['public']['Tables']['projects']['Row'];

export default function DeliverableCard({
  deliverable,
  project,
  projectRole,
}: {
  deliverable: DeliverableWithTeam;
  project: Project;
  projectRole: ProjectRole;
}) {
  const fetcher = useFetcher();
  const teamStatus = deliverable.is_ingesting
    ? 'processing'
    : (optimisticTeamStatus(fetcher.formData) ??
      deliverable.deliverables_team?.team_status ??
      'pending');
  const teamReviewUrl = deliverable.deliverables_team?.frame_io_team_review_url;
  const clientReviewUrl = deliverable.frame_io_client_review_url;
  const latestVersion = deliverable.deliverables_team?.latest_version_display;
  const { label, colour, pulse } = getTeamStatusDisplay(
    teamStatus,
    projectRole
  );

  function handleNeedsRevision() {
    fetcher.submit(
      {
        intent: 'teamNeedsRevision',
        deliverableId: deliverable.id,
      },
      { method: 'PATCH' }
    );
  }

  function handleApproved() {
    toast.info(
      'Preparing and sending to client.',
      {
        id: `promote-${deliverable.id}`,
      }
    );
    fetcher.submit(
      {
        intent: 'teamApprove',
        deliverableId: deliverable.id,
      },
      { method: 'PATCH' }
    );
  }

  const result = fetcher.data as ActionResult | undefined;

  useEffect(() => {
    if (!result) return;
    if (result.intent === 'teamNeedsRevision') {
      switch (result.result) {
        case 'sent':
          toast.success('Sent back for revision.', {
          });
          break;
        case 'failed':
          toast.error('Failed to submit for revision.', {
            duration: Infinity,
            closeButton: true,
          });
          break;
      }
    } else {
      switch (result.result) {
        case 'started':
          toast.success('Sent to client.', {
            id: `promote-${deliverable.id}`,
          });
          break;
        case 'in_progress':
          toast.info(
            'Already sending to client, this can take a few minutes.',
            {
              id: `promote-${deliverable.id}`,
            }
          );
          break;
        case 'source_processing':
          toast.info('The previous version is still being processed.', {
            id: `promote-${deliverable.id}`,
          });
          break;
        case 'team_processing':
          toast.info('The latest upload is still processing. Try again soon.', {
            id: `promote-${deliverable.id}`,
          });
          break;
        case 'team_reupload':
          toast.error('The latest upload failed or is missing.', {
            closeButton: true,
            duration: Infinity,
            id: `promote-${deliverable.id}`,
          });
          break;
        case 'failed':
          toast.error('Failed to send to client.', {
            duration: Infinity,
            closeButton: true,
            id: `promote-${deliverable.id}`,
          });
          break;
        case 'unknown':
          toast.info('Still sending to client. This can take a few minutes.', {
            id: `promote-${deliverable.id}`,
          });
          break;
      }
    }
  }, [result]);

  const isEligible = teamStatus === 'team_review';
  const isSubmitting = fetcher.state !== 'idle';
  const isPromoting = deliverable.is_promoting;

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="flex flex-row gap-3 text-base">
          <StatusBadge label={label} colour={colour} pulse={pulse} />
          <Separator orientation="vertical" className="h-6! w-0.5!" />
          <span>{deliverable.deliverable_name}</span>
        </CardTitle>
        <CardAction className="flex flex-row gap-4 items-center">
          <ButtonGroup>
            <Button
              size={'sm'}
              variant={'outline'}
              className="cursor-pointer"
              disabled={!teamReviewUrl}
              asChild={!!teamReviewUrl}
            >
              {teamReviewUrl ? (
                <a
                  href={teamReviewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Team Review
                </a>
              ) : (
                'Team Review'
              )}
            </Button>
            <Button
              size={'sm'}
              variant={'outline'}
              className="cursor-pointer"
              disabled={!clientReviewUrl}
              asChild={!!clientReviewUrl}
            >
              {clientReviewUrl ? (
                <a
                  href={clientReviewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Client Review
                </a>
              ) : (
                'Client Review'
              )}
            </Button>
          </ButtonGroup>
          <Separator orientation="vertical" className="h-8! w-0.5!" />
          {projectRole === 'editor' ? (
            <>
              <UploadVersion deliverable={deliverable} project={project} />
            </>
          ) : (
            <>
              <ButtonGroup>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button
                      variant={'outline'}
                      size={'sm'}
                      className="cursor-pointer"
                      disabled={!isEligible || isSubmitting || isPromoting}
                    >
                      Needs Revision
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogTitle>Send back to the Editor?</DialogTitle>
                    <DialogDescription>
                      This action cannot be undone.
                    </DialogDescription>
                    <div className="flex flex-row gap-2 justify-end">
                      <DialogClose asChild>
                        <Button variant={'outline'} className="cursor-pointer">
                          Cancel
                        </Button>
                      </DialogClose>
                      <DialogClose asChild>
                        <Button
                          className="cursor-pointer"
                          onClick={handleNeedsRevision}
                        >
                          Confirm
                        </Button>
                      </DialogClose>
                    </div>
                  </DialogContent>
                </Dialog>

                <Dialog>
                  <DialogTrigger asChild>
                    <Button
                      variant={'outline'}
                      size={'sm'}
                      className="cursor-pointer"
                      disabled={!isEligible || isSubmitting || isPromoting}
                    >
                      Approved
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogTitle>Send to Client for review?</DialogTitle>
                    <DialogDescription>
                      This action cannot be undone.
                    </DialogDescription>
                    <div className="flex flex-row gap-2 justify-end">
                      <DialogClose asChild>
                        <Button variant={'outline'} className="cursor-pointer">
                          Cancel
                        </Button>
                      </DialogClose>
                      <DialogClose asChild>
                        <Button
                          className="cursor-pointer"
                          onClick={handleApproved}
                        >
                          Confirm
                        </Button>
                      </DialogClose>
                    </div>
                  </DialogContent>
                </Dialog>
              </ButtonGroup>
            </>
          )}
        </CardAction>
      </CardHeader>
      <CardContent>
        <div className="flex flex-row gap-8 w-full text-xs">
          {latestVersion ? (
            <Tooltip delayDuration={800}>
              <TooltipTrigger className="flex flex-row gap-3 items-center">
                <span className="text-right">
                  <Layers className="w-4 h-4" />
                </span>
                <span>V{latestVersion}</span>
              </TooltipTrigger>
              <TooltipContent>
                <p>Latest Version</p>
              </TooltipContent>
            </Tooltip>
          ) : null}

          <Tooltip delayDuration={800}>
            <TooltipTrigger className="flex flex-row gap-3 items-center">
              <span className="text-right">
                <Video className="w-4 h-4" />
              </span>
              <span>{deliverable.video_type}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Video Type</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip delayDuration={800}>
            <TooltipTrigger className="flex flex-row gap-3 items-center">
              <span className="text-right">
                <Timer className="w-4 h-4" />
              </span>
              <span>{deliverable.length_range}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Length Range</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip delayDuration={800}>
            <TooltipTrigger className="flex flex-row gap-3 items-center">
              <span className="text-right">
                <Proportions className="w-4 h-4" />
              </span>
              <span>{deliverable.aspect_ratio}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Aspect Ratio</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip delayDuration={800}>
            <TooltipTrigger className="flex flex-row gap-3 items-center">
              <span className="text-right">
                <Hd className="w-4 h-4" />
              </span>
              <span>{deliverable.resolution}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Resolution</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip delayDuration={800}>
            <TooltipTrigger className="flex flex-row gap-3 items-center">
              <span className="text-right">
                <Play className="w-4 h-4" />
              </span>
              <span>{deliverable.frame_rate}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Frame Rate</p>
            </TooltipContent>
          </Tooltip>
        </div>
        {deliverable.notes && (
          <div className='mt-5'>
            <span className="text-sm text-muted-foreground">Notes</span>
            <MarkdownDisplay
              content={deliverable.notes}
              size='sm'
              variant='rich'
              className="mt-2"
              expandable={true}
              lines={5}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
